import apiClient from "src/config/ApiClient";
import { getTemplateGeneratorBaseUrl } from "src/config/Endpoints";
import i18n from "src/i18n/config";
import { getApiLangFromI18n, normalizeApiLang } from "src/utils/apiLang";
import type {
  CampaignDraft,
  CampaignDraftContent,
  CampaignDraftData,
  DraftChannel,
  SaveDraftRequest,
} from "./CampaignDraftTypes";
import { ApiError, getAuthToken } from "./HttpUtils";

const DRAFTS_URL = `${getTemplateGeneratorBaseUrl()}/drafts`;

const getRequestContext = () => {
  const tenantId = Number(localStorage.getItem("tenant_id"));
  if (!tenantId) {
    throw new Error("Tenant ID not found in localStorage");
  }
  return {
    tenantId,
    params: {
      lang: normalizeApiLang(getApiLangFromI18n(i18n)),
    },
    headers: {
      Authorization: `Bearer ${getAuthToken("api_key")}`,
    },
  };
};

const toApiError = (error: unknown): never => {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })
    ?.response;
  if (!response) {
    throw error;
  }
  const detail = response.data?.detail;
  throw new ApiError(typeof detail === "string" ? detail : "", {
    status: response.status,
    detail,
  });
};

export const CampaignDraftAPI = {
  async create(
    channel: DraftChannel,
    data: CampaignDraftData,
    content?: CampaignDraftContent | null
  ): Promise<CampaignDraft> {
    try {
      const { tenantId, params, headers } = getRequestContext();
      const response = await apiClient.post<CampaignDraft>(
        DRAFTS_URL,
        { tenant_id: tenantId, channel, data, ...(content !== undefined && { content }) },
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return toApiError(error);
    }
  },

  async save(draftId: number, request: SaveDraftRequest): Promise<CampaignDraft> {
    try {
      const { tenantId, params, headers } = getRequestContext();
      const response = await apiClient.put<CampaignDraft>(
        `${DRAFTS_URL}/${draftId}`,
        {
          tenant_id: tenantId,
          data: request.data,
          ...(request.content !== undefined && { content: request.content }),
          ...(request.expectedUpdatedAt && { expected_updated_at: request.expectedUpdatedAt }),
        },
        { params, headers }
      );
      return response.data;
    } catch (error) {
      return toApiError(error);
    }
  },

  async fetch(draftId: number): Promise<CampaignDraft> {
    try {
      const { tenantId, params, headers } = getRequestContext();
      const response = await apiClient.get<CampaignDraft>(`${DRAFTS_URL}/${draftId}`, {
        params: { ...params, tenant_id: tenantId },
        headers,
      });
      return response.data;
    } catch (error) {
      return toApiError(error);
    }
  },
};
